// src/components/ImportDataAdmin.jsx
import React, { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { supabase } from "../lib/supabaseClient";

const TARGET_FIELDS = [
  "title","artist","year","medium","typeOfArt","description",
  "dimensions",
  "dimensions_unit","dimensions_height","dimensions_width","dimensions_depth",
  "price_low","price_high",
  "acquisition_type","acquisition_date","acquisition_source","acquisition_price",
  "tags"
];

export default function ImportDataAdmin() {
  const [collectorEmail, setCollectorEmail] = useState("");
  const [collectionTitle, setCollectionTitle] = useState("");
  const [dedup, setDedup] = useState(true);

  const [, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const headers = useMemo(() => (rows[0] ? Object.keys(rows[0]) : []), [rows]);

  async function handleFile(f) {
    const data = await f.arrayBuffer();
    const wb = XLSX.read(data, { type: "array" });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const json = XLSX.utils.sheet_to_json(ws, { defval: "" });
    setRows(json);

    const auto = {};
    const lower = (s) => s.toString().trim().toLowerCase();
    TARGET_FIELDS.forEach((tf) => {
      const found = Object.keys(json[0] || {}).find((h) => lower(h) === lower(tf));
      if (found) auto[tf] = found;
    });
    setMapping(auto);
  }

  function buildPayload() {
    return rows.map((r) => {
      const obj = {};
      TARGET_FIELDS.forEach((tf) => {
        const src = mapping[tf];
        obj[tf] = src ? r[src] : "";
      });
      if (typeof obj.tags === "string" && obj.tags) {
        obj.tags = obj.tags.split(",").map((s) => s.trim()).filter(Boolean);
      }
      if (!obj.dimensions && (obj.dimensions_height || obj.dimensions_width)) {
        const u = obj.dimensions_unit || "cm";
        const h = obj.dimensions_height || "";
        const w = obj.dimensions_width || "";
        obj.dimensions = `${h}x${w} ${u}`.trim();
      }
      return obj;
    });
  }

  async function submit() {
    if (!collectorEmail) return alert("Email du collectionneur requis");
    if (!rows.length) return alert("Aucune donnée à importer");
    setBusy(true);
    setResult(null);
    try {
      const payload = {
        collector_email: collectorEmail.trim().toLowerCase(),
        collection_title: collectionTitle || null,
        dedup: !!dedup,
        mapping,
        rows: buildPayload(),
      };

      // Appel de l’Edge Function import_data avec le JWT courant
      const { data, error } = await supabase.functions.invoke("import_data", {
        body: payload,
      });
      setBusy(false);
      if (error) {
        setResult({ error: error.message || "Échec import" });
        return;
      }
      // data = { job_id, inserted, skipped, errors }
      setResult(data);
      alert(`Import OK — ${data.inserted} insérées, ${data.skipped} ignorées, ${data.errors} erreurs.`);
    } catch (e) {
      setBusy(false);
      setResult({ error: e.message || "Erreur inconnue" });
      console.error(e);
    }
  }

  return (
    <div className="space-y-6">
      <div className="bg-white border rounded p-4">
        <h2 className="text-lg font-semibold mb-3">Import d’œuvres (équipe Artkiv)</h2>

        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="block text-sm mb-1">Email du collectionneur *</label>
            <input
              className="w-full border rounded px-3 py-2"
              type="email"
              value={collectorEmail}
              onChange={(e) => setCollectorEmail(e.target.value)}
              placeholder="client@domaine.com"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Collection cible (optionnel)</label>
            <input
              className="w-full border rounded px-3 py-2"
              value={collectionTitle}
              onChange={(e) => setCollectionTitle(e.target.value)}
              placeholder="ex: Collection Moderne"
            />
            <p className="text-xs text-gray-500 mt-1">
              Créée automatiquement si absente ; sinon une collection par défaut sera utilisée.
            </p>
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={dedup}
                onChange={(e) => setDedup(e.target.checked)}
              />
              Ignorer les doublons (recommandé)
            </label>
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm mb-1">Fichier (.xlsx ou .csv)</label>
          <input
            type="file"
            accept=".xlsx,.csv"
            onChange={(e) => e.target.files?.[0] && (setFile(e.target.files[0]), handleFile(e.target.files[0]))}
          />
          <p className="text-xs text-gray-500 mt-1">
            Formats supportés : XLSX / CSV. Les médias (images/PDF) s’ajoutent ensuite sur chaque fiche.
          </p>
        </div>
      </div>

      {rows.length > 0 && (
        <>
          <div className="bg-white border rounded p-4">
            <h3 className="font-medium mb-3">Mapping des colonnes</h3>
            <div className="grid md:grid-cols-2 gap-2">
              {TARGET_FIELDS.map((tf) => (
                <div key={tf} className="flex items-center gap-2">
                  <div className="w-48 text-sm">{tf}</div>
                  <select
                    className="border rounded px-2 py-1 flex-1"
                    value={mapping[tf] || ""}
                    onChange={(e) =>
                      setMapping((m) => ({ ...m, [tf]: e.target.value || undefined }))
                    }
                  >
                    <option value="">— (laisser vide)</option>
                    {headers.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border rounded p-4">
            <h3 className="font-medium mb-3">Aperçu (5 premières lignes)</h3>
            <div className="overflow-auto">
              <table className="min-w-[900px] text-sm">
                <thead>
                  <tr>
                    {TARGET_FIELDS.map((h) => (
                      <th key={h} className="px-2 py-1 text-left border-b">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 5).map((r, i) => (
                    <tr key={i} className="border-t">
                      {TARGET_FIELDS.map((tf, j) => (
                        <td key={j} className="px-2 py-1">
                          {String(mapping[tf] ? r[mapping[tf]] : "")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4">
              <button className="btn-artsy btn-primary-artsy" onClick={submit} disabled={busy}>
                {busy ? "Importation…" : "Importer"}
              </button>
              {result?.error && <div className="text-red-600 text-sm mt-2">{result.error}</div>}
              {result?.job_id && (
                <div className="text-sm text-gray-700 mt-2">
                  Job #{result.job_id} — {result.inserted} insérées, {result.skipped} ignorées, {result.errors} erreurs.
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
